import React, { useState, useEffect, useCallback } from "react";
import {
  Search, Calendar, Layers, AlertCircle, ArrowRight, ArrowLeft,
  Check, Info, X, Shield, SlidersHorizontal, Play, Database,
  ExternalLink, CheckCircle2, ShieldAlert, TrendingUp
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "../../utils/api/axios";
import toast from "react-hot-toast";

interface ScopedReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  companyName: string;
}

/** ReportItem shape returned by /v2/reports/stats/:program_id */
interface ReportItem {
  report_id: number;
  report_title: string;
  severity_category: string;
  severity: number | string;
  cvss_score?: number;
  cvss_vector?: string;
  cvss_version?: string;
  computed_severity_category: string;
  state: string;
  category: string;
  retest_status: string | null;
  is_closed: boolean;
  /** added after merge — which program_id this finding belongs to */
  _program_id?: number;
  /** added after merge — which run_id this finding belongs to */
  _run_id?: number;
  /** added after merge — asset_id */
  _asset_id?: string | null;
  /** added after merge — target_ids */
  _target_ids?: string[];
  asset_id?: string | number | null;
  target_ids?: string[] | string | null;
  run_id?: number | null;
}

/** Stats shape returned by /v2/reports/stats/:program_id  */
interface ProgramStats {
  total: number;
  closed: number;
  open: number;
  businessAcceptRisk: number;
  nonActionable: number;
  awaitingFix: number;
  fixVerified: number;
  fixApproved: number;
  closeRetest: number;
  severityBreakdown: { critical: number; high: number; medium: number; low: number; info: number };
  reports: ReportItem[];
}

/** Merged & filtered stats used in the review step */
interface ReviewStats {
  total: number;
  closed: number;
  open: number;
  businessAcceptRisk: number;
  nonActionable: number;
  severityBreakdown: { critical: number; high: number; medium: number; low: number; info: number };
  reports: ReportItem[];
}

// ──────────────────────────────────────────────────────────────
// Shared status helpers — identical logic to PentestReportConfirmationModal
// ──────────────────────────────────────────────────────────────

const getDynamicSeverityCategory = (report: ReportItem) => {
  let category = (report.computed_severity_category || report.severity_category || "").toLowerCase();

  if (!category || category === "info" || category === "none") {
    const score = report.cvss_score !== undefined && report.cvss_score !== null
      ? Number(report.cvss_score)
      : (typeof report.severity === "number"
        ? report.severity
        : (typeof report.severity === "string" && !report.severity.startsWith("CVSS:") && !isNaN(parseFloat(report.severity))
          ? parseFloat(report.severity)
          : null));

    if (score !== null && !isNaN(score)) {
      if (score >= 9.0) category = "critical";
      else if (score >= 7.0) category = "high";
      else if (score >= 4.0) category = "medium";
      else if (score > 0) category = "low";
      else category = "info";
    }
  }

  if (!category) category = "info";

  return {
    label: category,
    color:
      category === "critical" ? "bg-red-100 text-red-800" :
      category === "high"     ? "bg-orange-100 text-orange-800" :
      category === "medium"   ? "bg-yellow-100 text-yellow-800" :
      category === "low"      ? "bg-blue-100 text-blue-800" :
                                "bg-gray-100 text-gray-800"
  };
};

const getCategorizedStatus = (report: ReportItem) => {
  if (report.is_closed) return { label: "Closed",         color: "bg-green-100 text-green-800" };
  const state = (report.state || "").trim().toLowerCase();
  if (state === "business accepts risk")         return { label: "Accept Risk",    color: "bg-yellow-100 text-yellow-800" };
  if (state === "business non actionable issue") return { label: "Non-Actionable", color: "bg-gray-100 text-gray-800" };
  return { label: "Open", color: "bg-orange-100 text-orange-800" };
};

// ──────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────

const TypeBadge: React.FC<{ type: string }> = ({ type }) => {
  const t = (type || "").toLowerCase();
  let cls = "bg-gray-100 text-gray-600";
  if (t.includes("web") || t.includes("url")) cls = "bg-green-100 text-green-700";
  else if (t.includes("api")) cls = "bg-blue-100 text-blue-700";
  else if (t.includes("android") || t.includes("ios") || t.includes("app")) cls = "bg-purple-100 text-purple-700";
  else if (t.includes("network") || t.includes("ip")) cls = "bg-orange-100 text-orange-700";
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide shrink-0 ${cls}`}>{type}</span>;
};

const Checkbox: React.FC<{ checked: boolean }> = ({ checked }) => (
  <div className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 transition-all duration-150 ${checked ? "border-blue-600 bg-blue-600" : "border-gray-300 bg-white hover:border-blue-400"}`}>
    {checked && (
      <motion.div initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 400, damping: 18 }}>
        <Check className="h-2.5 w-2.5 text-white stroke-[3.5]" />
      </motion.div>
    )}
  </div>
);

const CheckList: React.FC<{
  title: string; selected: number; search: string;
  onSearch: (v: string) => void; onSelectAll: () => void; onClear: () => void;
  emptyIcon: React.ReactNode; emptyLabel: string; emptyHint?: string;
  loading?: boolean; children?: React.ReactNode;
}> = ({ title, selected, search, onSearch, onSelectAll, onClear, emptyIcon, emptyLabel, emptyHint, loading, children }) => (
  <div className="flex flex-col rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm h-[400px]">
    <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 shrink-0">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-black uppercase tracking-widest text-gray-600">{title}</span>
        {selected > 0 && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700">{selected} selected</span>}
      </div>
      <div className="flex items-center gap-2 text-[11px] font-semibold">
        <button type="button" onClick={onSelectAll} className="text-blue-600 hover:text-blue-800 transition-colors">All</button>
        <span className="text-gray-300">|</span>
        <button type="button" onClick={onClear} className="text-gray-400 hover:text-red-500 transition-colors">Clear</button>
      </div>
    </div>
    <div className="px-3 py-2.5 border-b border-gray-100 shrink-0">
      <div className="relative flex items-center">
        <Search className="absolute left-2.5 h-3.5 w-3.5 text-gray-400" />
        <input type="text" value={search} onChange={e => onSearch(e.target.value)} placeholder="Search..."
          className="w-full rounded-lg border border-gray-200 bg-gray-50 pl-8 pr-3 py-1.5 text-[12px] text-gray-700 placeholder:text-gray-400 focus:border-blue-400 focus:bg-white focus:outline-none transition-colors" />
      </div>
    </div>
    <div className="flex-1 overflow-y-auto p-2 space-y-0.5 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-200">
      {loading ? (
        <div className="h-full flex flex-col items-center justify-center gap-2 py-12">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600" />
          <span className="text-xs text-gray-400">Loading…</span>
        </div>
      ) : !children ? (
        <div className="h-full flex flex-col items-center justify-center text-center py-12 gap-2 text-gray-300">
          {emptyIcon}
          <p className="text-xs font-semibold text-gray-500">{emptyLabel}</p>
          {emptyHint && <p className="text-[10px] text-gray-400 max-w-[190px] leading-relaxed">{emptyHint}</p>}
        </div>
      ) : children}
    </div>
  </div>
);

// ──────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────

export const ScopedReportModal: React.FC<ScopedReportModalProps> = ({ isOpen, onClose, companyId, companyName }) => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [scopingMethod, setScopingMethod] = useState<"programs" | "dates">("programs");
  const [reportSuffix, setReportSuffix] = useState("");
  const [includeUnmapped, setIncludeUnmapped] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [programs, setPrograms] = useState<any[]>([]);
  const [allRuns, setAllRuns] = useState<any[]>([]);
  const [allAssets, setAllAssets] = useState<any[]>([]);
  const [allTargets, setAllTargets] = useState<any[]>([]);

  const [selectedPrograms, setSelectedPrograms] = useState<number[]>([]);
  const [selectedRuns, setSelectedRuns] = useState<number[]>([]);
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [selectedTargets, setSelectedTargets] = useState<string[]>([]);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [programSearch, setProgramSearch] = useState("");
  const [runSearch, setRunSearch] = useState("");
  const [assetSearch, setAssetSearch] = useState("");
  const [targetSearch, setTargetSearch] = useState("");
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Review step state
  const [reviewStats, setReviewStats] = useState<ReviewStats | null>(null);
  const [loadingReview, setLoadingReview] = useState(false);
  const [reviewSearch, setReviewSearch] = useState("");
  const [reviewStatusFilter, setReviewStatusFilter] = useState<"all" | "open" | "closed" | "accept_risk" | "non_actionable">("all");
  const [reviewSeverityFilter, setReviewSeverityFilter] = useState<"all" | "critical" | "high" | "medium" | "low" | "info">("all");
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [highlightCheckbox, setHighlightCheckbox] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const fetchAll = async () => {
      setLoadingOptions(true);
      try {
        const r = await axios.get("/v2/automated-report/scoping-options", { params: { company_id: companyId } });
        if (r.data?.status === "success") {
          setAllAssets(r.data.data.assets || []);
          setAllTargets(r.data.data.targets || []);
          setAllRuns(r.data.data.runs || []);
        }
        const p = await axios.post("/v2/automated-report/getUserPrograms");
        if (p.data?.status === "success") {
          setPrograms((p.data.data || []).filter((x: any) => Number(x.company_id) === Number(companyId)));
        }
      } catch { toast.error("Failed to load scoping options"); }
      finally { setLoadingOptions(false); }
    };
    fetchAll();
  }, [isOpen, companyId]);

  useEffect(() => {
    if (scopingMethod !== "dates" || !startDate || !endDate) return;
    const fetchDated = async () => {
      setLoadingOptions(true);
      try {
        const r = await axios.get("/v2/automated-report/scoping-options", { params: { company_id: companyId, start_date: startDate, end_date: endDate } });
        if (r.data?.status === "success") {
          const { assets, targets, runs } = r.data.data;
          setAllAssets(assets || []); setAllTargets(targets || []); setAllRuns(runs || []);
          setSelectedAssets(assets?.map((a: any) => a.id) || []);
          setSelectedTargets(targets?.map((t: any) => t.target_id) || []);
          setSelectedRuns(runs?.map((r: any) => r.run_id) || []);
        }
      } catch { toast.error("Failed to update scoping options"); }
      finally { setLoadingOptions(false); }
    };
    fetchDated();
  }, [scopingMethod, startDate, endDate, companyId]);

  /** Fetch preview stats using same logic as createAutomatedReport backend */
  const fetchReviewStats = useCallback(async () => {
    setLoadingReview(true);
    setReviewStats(null);
    try {
      // Determine the program IDs to query
      let programIdsToQuery: number[] = selectedPrograms;
      if (scopingMethod === "dates") {
        const progIds = new Set<number>();
        allRuns.forEach(r => { if (selectedRuns.includes(r.run_id)) progIds.add(Number(r.program_id)); });
        programIdsToQuery = Array.from(progIds);
      }
      if (programIdsToQuery.length === 0) {
        setReviewStats({ total: 0, closed: 0, open: 0, businessAcceptRisk: 0, nonActionable: 0, severityBreakdown: { critical: 0, high: 0, medium: 0, low: 0, info: 0 }, reports: [] });
        return;
      }

      // Fetch stats per program — same as PentestReportConfirmationModal pattern
      const perProgramStats = await Promise.all(
        programIdsToQuery.map(async (pid) => {
          try {
            const res = await axios.get(`/v2/reports/stats/${pid}`);
            return (res.data as ProgramStats);
          } catch {
            return null;
          }
        })
      );

      // Merge all reports from all programs
      let mergedReports: ReportItem[] = [];
      perProgramStats.forEach((stats, i) => {
        if (!stats) return;
        const pid = programIdsToQuery[i];
        stats.reports.forEach(r => mergedReports.push({ ...r, _program_id: pid }));
      });

      // ── Apply the exact same in-memory filtering as createAutomatedReport ──

      // 1. If run_ids selected, filter by run_id (for programs scope; dates scope already filtered)
      if (scopingMethod === "programs" && selectedRuns.length > 0) {
        const runFilteredStats = await Promise.all(
          selectedRuns.map(async (runId) => {
            const run = allRuns.find(r => r.run_id === runId);
            if (!run || !selectedPrograms.map(Number).includes(Number(run.program_id))) return null;
            try {
              const res = await axios.get(`/v2/reports/stats/${run.program_id}?run_id=${runId}`);
              return (res.data as ProgramStats).reports.map(r => ({ ...r, _program_id: run.program_id, _run_id: runId }));
            } catch { return null; }
          })
        );
        mergedReports = runFilteredStats.filter(Boolean).flat() as ReportItem[];
      }

      // 2. Apply asset_ids / target_ids filter + include_unmapped
      const targetAssetIds = selectedTargets
        .map(tid => allTargets.find(t => t.target_id === tid)?.asset_id)
        .filter(Boolean) as string[];
      const relevantAssetIds = Array.from(
        new Set([...selectedAssets.map(String), ...targetAssetIds.map(String)])
      );

      // If includeUnmapped is false, filter out completely unmapped reports (no asset_id AND no target_ids)
      if (!includeUnmapped) {
        mergedReports = mergedReports.filter(r => {
          let rTargetIds: string[] = [];
          if (r.target_ids) {
            rTargetIds = Array.isArray(r.target_ids)
              ? r.target_ids.map(String)
              : (() => { try { return JSON.parse(r.target_ids as any).map(String); } catch { return []; } })();
          }
          const hasAsset = !!r.asset_id;
          const hasTargets = rTargetIds.length > 0;
          return hasAsset || hasTargets;
        });
      }

      if (selectedAssets.length > 0 || selectedTargets.length > 0) {
        mergedReports = mergedReports.filter(r => {
          let rTargetIds: string[] = [];
          if (r.target_ids) {
            rTargetIds = Array.isArray(r.target_ids)
              ? r.target_ids.map(String)
              : (() => { try { return JSON.parse(r.target_ids as any).map(String); } catch { return []; } })();
          }

          // ── Target filter (takes priority — OR logic: match if ANY selected target applies) ──
          if (selectedTargets.length > 0) {
            const hasOverlap = rTargetIds.some(tid =>
              selectedTargets.map(String).includes(String(tid))
            );
            if (hasOverlap) return true;

            // Unmapped reports (no target_ids) pass if includeUnmapped AND they belong to selected/relevant assets
            if (rTargetIds.length === 0 && includeUnmapped) {
              const assetIdStr = r.asset_id ? String(r.asset_id) : "";
              if (assetIdStr && relevantAssetIds.includes(assetIdStr)) return true;
            }
            return false;
          }

          // ── Asset-only filter (only when assets selected but no specific targets chosen) ──
          if (selectedAssets.length > 0) {
            const assetIdStr = r.asset_id ? String(r.asset_id) : "";
            if (assetIdStr && selectedAssets.map(String).includes(assetIdStr)) return true;
            return false;
          }

          return true;
        });
      }

      // 3. Deduplicate by report_id (a report may appear in multiple run queries)
      const seen = new Set<number>();
      const dedupedReports: ReportItem[] = [];
      mergedReports.forEach(r => {
        if (!seen.has(r.report_id)) { seen.add(r.report_id); dedupedReports.push(r); }
      });

      // 4. Recompute aggregate stats from deduped reports (same as PentestReportConfirmationModal)
      const merged: ReviewStats = {
        total: dedupedReports.length,
        closed: 0, open: 0, businessAcceptRisk: 0, nonActionable: 0,
        severityBreakdown: { critical: 0, high: 0, medium: 0, low: 0, info: 0 },
        reports: dedupedReports
      };
      dedupedReports.forEach(r => {
        const status = getCategorizedStatus(r);
        if (status.label === "Closed") merged.closed++;
        else if (status.label === "Accept Risk") merged.businessAcceptRisk++;
        else if (status.label === "Non-Actionable") merged.nonActionable++;
        else merged.open++;
        const sev = (r.computed_severity_category || r.severity_category || "info").toLowerCase() as keyof typeof merged.severityBreakdown;
        if (sev in merged.severityBreakdown) merged.severityBreakdown[sev]++;
      });

      setReviewStats(merged);
    } catch (err) {
      toast.error("Failed to load review statistics");
    } finally {
      setLoadingReview(false);
    }
  }, [selectedPrograms, selectedRuns, selectedAssets, selectedTargets, scopingMethod, allRuns, includeUnmapped]);

  // Auto-fetch when reaching step 4
  useEffect(() => {
    if (step === 4) {
      setIsConfirmed(false);
      setReviewSearch("");
      setReviewStatusFilter("all");
      setReviewSeverityFilter("all");
      fetchReviewStats();
    }
  }, [step]);

  if (!isOpen) return null;

  // ── Selection helpers ──

  const handleProgramToggle = (id: number) => {
    if (selectedPrograms.includes(id)) {
      setSelectedPrograms(selectedPrograms.filter(x => x !== id));
      setSelectedRuns(selectedRuns.filter(rid => Number(allRuns.find(r => r.run_id === rid)?.program_id) !== id));
    } else {
      setSelectedPrograms([...selectedPrograms, id]);
    }
  };
  const handleRunToggle = (id: number) =>
    setSelectedRuns(selectedRuns.includes(id) ? selectedRuns.filter(x => x !== id) : [...selectedRuns, id]);
  const handleAssetToggle = (id: string) => {
    if (selectedAssets.includes(id)) {
      setSelectedAssets(selectedAssets.filter(x => x !== id));
      setSelectedTargets(selectedTargets.filter(tid => allTargets.find(t => t.target_id === tid)?.asset_id !== id));
    } else {
      setSelectedAssets([...selectedAssets, id]);
      const toAdd = allTargets.filter(t => t.asset_id === id).map(t => t.target_id);
      setSelectedTargets(Array.from(new Set([...selectedTargets, ...toAdd])));
    }
  };
  const handleTargetToggle = (id: string) =>
    setSelectedTargets(selectedTargets.includes(id) ? selectedTargets.filter(x => x !== id) : [...selectedTargets, id]);

  const validateSuffix = () => {
    const s = reportSuffix.trim().toUpperCase();
    if (!s) { toast.error("A report suffix is required to isolate this report series."); return false; }
    if (s !== s.replace(/[^A-Z0-9_-]/g, "")) { toast.error("Only letters, numbers, hyphens, and underscores are allowed."); return false; }
    return true;
  };

  const handleNext = () => {
    if (step === 1) {
      if (!validateSuffix()) return;
      setStep(2);
    } else if (step === 2) {
      if (scopingMethod === "programs") {
        if (!selectedPrograms.length) { toast.error("Select at least one program"); return; }
        if (allRuns.filter(r => selectedPrograms.includes(Number(r.program_id))).length && !selectedRuns.length) {
          toast.error("Select at least one completed run"); return;
        }
      } else {
        if (!startDate || !endDate) { toast.error("Select both start and end dates"); return; }
        if (new Date(startDate) > new Date(endDate)) { toast.error("Start date cannot be after end date"); return; }
      }
      setStep(3);
    } else if (step === 3) {
      setStep(4);
    }
  };

  const handleSubmit = async () => {
    if (!isConfirmed) {
      setHighlightCheckbox(true);
      setTimeout(() => setHighlightCheckbox(false), 3000);
      toast.error("Please confirm you have reviewed the findings above.");
      return;
    }
    if (!validateSuffix()) return;
    let requestProgramIds = selectedPrograms;
    if (scopingMethod === "dates") {
      const progIds = new Set<number>();
      allRuns.forEach(r => { if (selectedRuns.includes(r.run_id)) progIds.add(Number(r.program_id)); });
      requestProgramIds = Array.from(progIds);
      if (!requestProgramIds.length) { toast.error("No completed runs found in this date range"); return; }
    } else {
      if (!selectedPrograms.length) { toast.error("Select at least one program"); return; }
    }
    setSubmitting(true);
    const tid = toast.loading("Submitting report request…");
    try {
      const res = await axios.post("/v2/automated-report/create", {
        company_name: companyName,
        program_ids: requestProgramIds,
        run_ids: selectedRuns.length ? selectedRuns : null,
        asset_ids: selectedAssets.length ? selectedAssets : null,
        target_ids: selectedTargets.length ? selectedTargets : null,
        start_date: scopingMethod === "dates" ? startDate : null,
        end_date: scopingMethod === "dates" ? endDate : null,
        include_unmapped: includeUnmapped,
        report_suffix: reportSuffix.trim().toUpperCase()
      });
      if (res.data?.status === "success") {
        toast.success(res.data.message || "Scoped report requested!", { id: tid });
        onClose();
        window.location.reload();
      } else {
        toast.error(res.data?.message || "Failed to create report", { id: tid });
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Server error", { id: tid });
    } finally { setSubmitting(false); }
  };

  // ── Filtered lists for steps 2–3 ──

  const filteredPrograms = programs.filter(p =>
    p.program_title?.toLowerCase().includes(programSearch.toLowerCase()) || String(p.program_id).includes(programSearch));
  const activeRuns = allRuns.filter(r => selectedPrograms.includes(Number(r.program_id)));
  const filteredRuns = activeRuns.filter(r => (r.run_title || `Run #${r.run_id}`).toLowerCase().includes(runSearch.toLowerCase()));

  // Dynamically collect assets and targets linked to selected programs and runs
  const dynamicScope = (() => {
    let activeRunsToParse = [];
    if (scopingMethod === "programs") {
      if (selectedRuns.length > 0) {
        activeRunsToParse = allRuns.filter(r => selectedRuns.includes(r.run_id));
      } else {
        activeRunsToParse = allRuns.filter(r => selectedPrograms.includes(Number(r.program_id)));
      }
    } else {
      if (selectedRuns.length > 0) {
        activeRunsToParse = allRuns.filter(r => selectedRuns.includes(r.run_id));
      } else {
        activeRunsToParse = allRuns;
      }
    }

    const assetIds = new Set<string>();
    const targetIds = new Set<string>();
    const hasRuns = activeRunsToParse.length > 0;

    activeRunsToParse.forEach(run => {
      if (run.settings_snapshot) {
        try {
          const snapshot = typeof run.settings_snapshot === "string"
            ? JSON.parse(run.settings_snapshot)
            : run.settings_snapshot;
          if (snapshot.asset?.id) {
            assetIds.add(snapshot.asset.id);
          }
          if (Array.isArray(snapshot.targets)) {
            snapshot.targets.forEach((t: any) => {
              if (t.target_id) targetIds.add(t.target_id);
            });
          }
        } catch (e) {
          console.error("Error parsing settings_snapshot:", e);
        }
      }
    });

    return { assetIds, targetIds, hasRuns };
  })();

  const filteredAssets = allAssets.filter(a => {
    const matchesSearch = a.name?.toLowerCase().includes(assetSearch.toLowerCase());
    if (dynamicScope.hasRuns) {
      return matchesSearch && dynamicScope.assetIds.has(a.id);
    }
    return matchesSearch;
  });

  const filteredTargets = allTargets.filter(t => {
    const matchesSearch =
      (t.target_name || "").toLowerCase().includes(targetSearch.toLowerCase()) ||
      (t.target_value || "").toLowerCase().includes(targetSearch.toLowerCase()) ||
      (t.target_type || "").toLowerCase().includes(targetSearch.toLowerCase());
    if (dynamicScope.hasRuns) {
      return matchesSearch && dynamicScope.targetIds.has(t.target_id);
    }
    return matchesSearch;
  });

  // ── Filtered reports for review step ──

  const filteredReviewReports = (reviewStats?.reports || []).filter(r => {
    const titleMatch = (r.report_title || "").toLowerCase().includes(reviewSearch.toLowerCase()) || String(r.report_id).includes(reviewSearch);
    const status = getCategorizedStatus(r);
    const statusMatch =
      reviewStatusFilter === "all" ? true :
      reviewStatusFilter === "open" ? status.label === "Open" :
      reviewStatusFilter === "closed" ? status.label === "Closed" :
      reviewStatusFilter === "accept_risk" ? status.label === "Accept Risk" :
      status.label === "Non-Actionable";
    const sev = (r.computed_severity_category || r.severity_category || "info").toLowerCase();
    const sevMatch = reviewSeverityFilter === "all" ? true : sev === reviewSeverityFilter;
    return titleMatch && statusMatch && sevMatch;
  });

  // ── Bulk ops ──

  const selectAllPrograms = () => setSelectedPrograms(Array.from(new Set([...selectedPrograms, ...filteredPrograms.map(p => Number(p.program_id))])));
  const clearPrograms = () => {
    const ids = filteredPrograms.map(p => Number(p.program_id));
    setSelectedPrograms(selectedPrograms.filter(x => !ids.includes(x)));
    setSelectedRuns(selectedRuns.filter(rid => !ids.includes(Number(allRuns.find(r => r.run_id === rid)?.program_id))));
  };
  const selectAllRuns = () => setSelectedRuns(Array.from(new Set([...selectedRuns, ...filteredRuns.map(r => r.run_id)])));
  const clearRuns = () => { const ids = filteredRuns.map(r => r.run_id); setSelectedRuns(selectedRuns.filter(x => !ids.includes(x))); };
  const selectAllAssets = () => {
    const ids = filteredAssets.map(a => a.id);
    setSelectedAssets(Array.from(new Set([...selectedAssets, ...ids])));
    setSelectedTargets(Array.from(new Set([...selectedTargets, ...allTargets.filter(t => ids.includes(t.asset_id)).map(t => t.target_id)])));
  };
  const clearAssets = () => {
    const ids = filteredAssets.map(a => a.id);
    setSelectedAssets(selectedAssets.filter(x => !ids.includes(x)));
    setSelectedTargets(selectedTargets.filter(tid => !ids.includes(allTargets.find(t => t.target_id === tid)?.asset_id)));
  };
  const selectAllTargets = () => setSelectedTargets(Array.from(new Set([...selectedTargets, ...filteredTargets.map(t => t.target_id)])));
  const clearTargets = () => { const ids = filteredTargets.map(t => t.target_id); setSelectedTargets(selectedTargets.filter(x => !ids.includes(x))); };

  const STEPS = ["Identity & Method", "Scope Source", "Fine Scoping", "Review & Submit"];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <motion.div
        initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 280, damping: 26 }}
        className="relative z-50 w-full max-w-5xl flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden border border-gray-200"
        style={{ maxHeight: "94vh" }}
      >
        {/* ── HEADER ── */}
        <div className="shrink-0 px-7 py-5 flex items-center justify-between" style={{ background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)" }}>
          <div className="flex items-center gap-3.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 border border-white/20 shrink-0">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white leading-tight">Create Scoped / Combined Report</h2>
              <p className="text-[12px] text-blue-100 mt-0.5">Configure target checkpoints and asset boundaries · <span className="font-semibold text-white">{companyName}</span></p>
            </div>
          </div>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-lg text-white/70 hover:bg-white/15 hover:text-white transition-all">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ── STEPPER ── */}
        <div className="shrink-0 bg-gray-50 border-b border-gray-200 px-7 py-4">
          <div className="flex items-center justify-center gap-0 w-full max-w-2xl mx-auto">
            {STEPS.map((label, i) => {
              const n = i + 1;
              const done = step > n;
              const active = step === n;
              return (
                <React.Fragment key={n}>
                  <button type="button" onClick={() => { if (n < step) setStep(n); }} className="flex flex-col items-center gap-1 group">
                    <div className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-bold transition-all ${done ? "bg-blue-600 border-blue-600 text-white" : active ? "bg-white border-blue-600 text-blue-600 shadow-[0_0_0_4px_rgba(59,130,246,0.15)]" : "bg-white border-gray-300 text-gray-400 cursor-default"}`}>
                      {done ? <Check className="h-4 w-4 stroke-[3]" /> : n}
                    </div>
                    <span className={`text-[11px] font-semibold whitespace-nowrap ${active ? "text-blue-600" : done ? "text-gray-600" : "text-gray-400"}`}>{label}</span>
                  </button>
                  {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 mb-5 rounded-full transition-all ${step > n ? "bg-blue-600" : "bg-gray-200"}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* ── BODY ── */}
        <div className="flex-1 overflow-y-auto px-7 py-6 bg-white [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-200">
          <AnimatePresence mode="wait">

            {/* STEP 1 — Identity & Method */}
            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -14 }} transition={{ duration: 0.18 }} className="space-y-5">
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <SlidersHorizontal className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-bold text-gray-800">Report Suffix</span>
                    <span className="ml-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-600 uppercase tracking-wide">Required</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="md:col-span-2 space-y-2">
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-500">Custom Suffix</label>
                      <input type="text" placeholder="e.g. BRANDA, PRODUCT-X, DEPT-B" value={reportSuffix} onChange={e => setReportSuffix(e.target.value.toUpperCase())}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-sm font-semibold tracking-widest placeholder:font-normal placeholder:tracking-normal placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/10 transition-all" />
                      <p className="text-[10px] text-gray-400">Letters · numbers · hyphens · underscores</p>
                    </div>
                    <div className="md:col-span-3 rounded-lg bg-blue-50 border border-blue-100 p-3.5 flex gap-2.5">
                      <Info className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                      <div className="text-[12px] text-gray-600 leading-relaxed">
                        <span className="font-bold text-gray-800">Why is a suffix required?</span>
                        <p className="mt-0.5">The suffix namespaces this report series. Drafts, reviews, and version chains are tracked independently — preventing approvals from colliding with the main program report.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2.5">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Scoping Method</p>
                  <div className="grid grid-cols-2 gap-4">
                    {([
                      { id: "programs", icon: <Layers className="h-5 w-5" />, label: "By Programs & Runs", hint: "Select specific programs and testing cycles" },
                      { id: "dates", icon: <Calendar className="h-5 w-5" />, label: "By Custom Date Range", hint: "Include findings from runs completed in a date window" },
                    ] as const).map(opt => (
                      <button key={opt.id} type="button" onClick={() => { setScopingMethod(opt.id); if (opt.id === "programs") { setStartDate(""); setEndDate(""); setSelectedRuns([]); } else { setSelectedPrograms([]); setSelectedRuns([]); } }}
                        className={`flex items-center gap-3.5 p-4 rounded-xl border-2 text-left transition-all ${scopingMethod === opt.id ? "border-blue-600 bg-blue-50 shadow-sm" : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"}`}>
                        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-all ${scopingMethod === opt.id ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-500"}`}>{opt.icon}</div>
                        <div>
                          <div className={`text-sm font-bold ${scopingMethod === opt.id ? "text-blue-900" : "text-gray-800"}`}>{opt.label}</div>
                          <div className="text-[11px] text-gray-500 mt-0.5">{opt.hint}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div onClick={() => setIncludeUnmapped(!includeUnmapped)}
                  className={`flex items-start gap-3.5 rounded-xl border-2 p-4 cursor-pointer transition-all select-none ${includeUnmapped ? "border-blue-200 bg-blue-50/40" : "border-gray-200 bg-white hover:border-gray-300"}`}>
                  <div className="mt-0.5"><Checkbox checked={includeUnmapped} /></div>
                  <div>
                    <p className="text-sm font-bold text-gray-800">Include unmapped findings</p>
                    <p className="text-[12px] text-gray-500 mt-0.5 leading-relaxed">Historical findings without explicitly mapped target/asset context will still be included in the report when they match the selected programs or date range.</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* STEP 2 — Scope Source */}
            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -14 }} transition={{ duration: 0.18 }}>
                {scopingMethod === "programs" ? (
                  <div className="grid grid-cols-2 gap-5">
                    <CheckList title="Programs" selected={selectedPrograms.length} search={programSearch} onSearch={setProgramSearch} onSelectAll={selectAllPrograms} onClear={clearPrograms} emptyIcon={<Layers className="h-9 w-9" />} emptyLabel="No programs found" loading={loadingOptions && programs.length === 0}>
                      {filteredPrograms.length > 0 ? <>{filteredPrograms.map(prog => { const sel = selectedPrograms.includes(prog.program_id); return (
                        <div key={prog.program_id} onClick={() => handleProgramToggle(prog.program_id)} className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 cursor-pointer transition-all ${sel ? "bg-blue-50 border border-blue-200" : "hover:bg-gray-50 border border-transparent"}`}>
                          <Checkbox checked={sel} />
                          <div className="min-w-0 flex-1">
                            <p className="text-[12px] font-semibold text-gray-800 truncate">{prog.program_title}</p>
                            <p className="text-[10px] text-gray-400">#{prog.program_id}</p>
                          </div>
                        </div>
                      ); })}</> : <div className="py-10 text-center text-[11px] text-gray-400 italic">No programs match</div>}
                    </CheckList>

                    <CheckList title="Completed Runs" selected={selectedRuns.length} search={runSearch} onSearch={setRunSearch} onSelectAll={selectAllRuns} onClear={clearRuns} emptyIcon={<Play className="h-9 w-9" />} emptyLabel={selectedPrograms.length === 0 ? "No program selected" : "No runs found"} emptyHint={selectedPrograms.length === 0 ? "Select a program on the left to see its completed runs." : undefined}>
                      {selectedPrograms.length > 0 && filteredRuns.length > 0 ? <>{filteredRuns.map(run => { const sel = selectedRuns.includes(run.run_id); return (
                        <div key={run.run_id} onClick={() => handleRunToggle(run.run_id)} className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 cursor-pointer transition-all ${sel ? "bg-blue-50 border border-blue-200" : "hover:bg-gray-50 border border-transparent"}`}>
                          <Checkbox checked={sel} />
                          <div className="min-w-0 flex-1">
                            <p className="text-[12px] font-semibold text-gray-800 truncate">{run.run_title || `Run #${run.run_id}`}</p>
                            {run.end_date && <p className="text-[10px] text-gray-400">Ended {new Date(run.end_date).toLocaleDateString()}</p>}
                          </div>
                        </div>
                      ); })}</> : selectedPrograms.length > 0 ? <div className="py-10 text-center text-[11px] text-gray-400 italic">No runs match</div> : null}
                    </CheckList>
                  </div>
                ) : (
                  <div className="rounded-xl border border-gray-200 bg-white p-6 space-y-5 shadow-sm">
                    <div className="flex items-center gap-2 text-sm font-bold text-gray-800 pb-3 border-b border-gray-100">
                      <Calendar className="h-4 w-4 text-blue-600" /> Select Date Window
                    </div>
                    <div className="grid grid-cols-2 gap-5">
                      {[["Start Date", startDate, setStartDate], ["End Date", endDate, setEndDate]].map(([lbl, val, setter]: any) => (
                        <div key={lbl} className="space-y-2">
                          <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-500">{lbl}</label>
                          <input type="date" value={val} onChange={e => setter(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/10 transition-all" />
                        </div>
                      ))}
                    </div>
                    <div className="flex items-start gap-2.5 rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 text-[12px] text-gray-600">
                      <Info className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                      <span>Only testing runs completed within this window will be scanned. Assets and targets are dynamically computed from those runs.</span>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* STEP 3 — Fine Scoping */}
            {step === 3 && (
              <motion.div key="s3" initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -14 }} transition={{ duration: 0.18 }} className="space-y-4">
                <div className="flex items-start gap-2.5 rounded-lg bg-yellow-50 border border-yellow-200 px-4 py-3 text-[12px] text-yellow-800">
                  <Info className="h-4 w-4 text-yellow-600 shrink-0 mt-0.5" />
                  <span><b>Optional.</b> Leave all unchecked to include every asset and target from your selected scope automatically.</span>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  <CheckList title="Assets" selected={selectedAssets.length} search={assetSearch} onSearch={setAssetSearch} onSelectAll={selectAllAssets} onClear={clearAssets} emptyIcon={<Database className="h-9 w-9" />} emptyLabel="No assets available" loading={loadingOptions}>
                    {filteredAssets.length > 0 ? <>{filteredAssets.map(asset => { const sel = selectedAssets.includes(asset.id); return (
                      <div key={asset.id} onClick={() => handleAssetToggle(asset.id)} className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 cursor-pointer transition-all ${sel ? "bg-blue-50 border border-blue-200" : "hover:bg-gray-50 border border-transparent"}`}>
                        <Checkbox checked={sel} />
                        <p className="text-[12px] font-semibold text-gray-800 truncate">{asset.name}</p>
                      </div>
                    ); })}</> : <div className="py-10 text-center text-[11px] text-gray-400 italic">No assets match</div>}
                  </CheckList>

                  <CheckList title="Targets" selected={selectedTargets.length} search={targetSearch} onSearch={setTargetSearch} onSelectAll={selectAllTargets} onClear={clearTargets} emptyIcon={<AlertCircle className="h-9 w-9" />} emptyLabel="No targets available" loading={loadingOptions}>
                    {filteredTargets.length > 0 ? <>{filteredTargets.map(target => { const sel = selectedTargets.includes(target.target_id); return (
                      <div key={target.target_id} onClick={() => handleTargetToggle(target.target_id)} className={`flex items-start gap-2.5 rounded-lg px-3 py-2.5 cursor-pointer transition-all ${sel ? "bg-blue-50 border border-blue-200" : "hover:bg-gray-50 border border-transparent"}`}>
                        <div className="mt-0.5"><Checkbox checked={sel} /></div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[12px] font-semibold text-gray-800 leading-snug">{target.target_name || "Unnamed Target"}</p>
                          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                            <TypeBadge type={target.target_type} />
                            <span className="text-[10px] text-gray-400 truncate max-w-[170px]" title={target.target_value}>{target.target_value}</span>
                          </div>
                        </div>
                      </div>
                    ); })}</> : <div className="py-10 text-center text-[11px] text-gray-400 italic">No targets match</div>}
                  </CheckList>
                </div>
              </motion.div>
            )}

            {/* STEP 4 — Review & Submit */}
            {step === 4 && (
              <motion.div key="s4" initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -14 }} transition={{ duration: 0.18 }} className="space-y-5">

                {loadingReview ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
                    <p className="text-sm text-gray-500">Loading report preview…</p>
                    <p className="text-[11px] text-gray-400">Fetching findings that will be included in this scoped report</p>
                  </div>
                ) : !reviewStats ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <AlertCircle className="h-10 w-10 text-red-400" />
                    <p className="text-sm font-semibold text-gray-600">Could not load preview</p>
                    <button type="button" onClick={fetchReviewStats} className="text-[12px] text-blue-600 hover:underline">Retry</button>
                  </div>
                ) : (
                  <>
                    {/* Configuration summary banner */}
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-3">Configuration Summary</p>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-[11px] font-semibold text-blue-800">
                          <SlidersHorizontal className="h-3.5 w-3.5" /> Suffix: {reportSuffix}
                        </span>
                        <span className="inline-flex items-center gap-1.5 rounded-lg border border-purple-200 bg-purple-50 px-3 py-1.5 text-[11px] font-semibold text-purple-800">
                          {scopingMethod === "programs" ? <Layers className="h-3.5 w-3.5" /> : <Calendar className="h-3.5 w-3.5" />}
                          {scopingMethod === "programs" ? `${selectedPrograms.length} program(s) · ${selectedRuns.length} run(s)` : `${startDate} → ${endDate}`}
                        </span>
                        {selectedAssets.length > 0 && (
                          <span className="inline-flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-50 px-3 py-1.5 text-[11px] font-semibold text-green-800">
                            <Database className="h-3.5 w-3.5" /> {selectedAssets.length} asset(s)
                          </span>
                        )}
                        {selectedTargets.length > 0 && (
                          <span className="inline-flex items-center gap-1.5 rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-[11px] font-semibold text-orange-800">
                            <Shield className="h-3.5 w-3.5" /> {selectedTargets.length} target(s)
                          </span>
                        )}
                        {includeUnmapped && (
                          <span className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-gray-600">
                            <Check className="h-3.5 w-3.5 text-green-600" /> Unmapped included
                          </span>
                        )}
                      </div>
                    </div>

                    {/* 4 status cards — identical to PentestReportConfirmationModal */}
                    <div className="grid grid-cols-4 gap-3">
                      {[
                        { label: "Closed", count: reviewStats.closed, icon: <CheckCircle2 className="h-5 w-5" />, from: "from-green-50", to: "to-green-100/50", text: "text-green-700", num: "text-green-900", border: "border-gray-200" },
                        { label: "Open", count: reviewStats.open, icon: <AlertCircle className="h-5 w-5" />, from: "from-orange-50", to: "to-orange-100/50", text: "text-orange-700", num: "text-orange-900", border: "border-gray-200" },
                        { label: "Accept Risk", count: reviewStats.businessAcceptRisk, icon: <ShieldAlert className="h-5 w-5" />, from: "from-yellow-50", to: "to-yellow-100/50", text: "text-yellow-700", num: "text-yellow-900", border: "border-gray-200" },
                        { label: "Non-Actionable", count: reviewStats.nonActionable, icon: <Info className="h-5 w-5" />, from: "from-gray-50", to: "to-gray-100/50", text: "text-gray-700", num: "text-gray-900", border: "border-gray-200" },
                      ].map(card => (
                        <div key={card.label} className={`rounded-xl border ${card.border} bg-gradient-to-br ${card.from} ${card.to} p-4`}>
                          <div className={`flex items-center gap-2 ${card.text}`}>
                            {card.icon}
                            <span className="text-sm font-medium">{card.label}</span>
                          </div>
                          <p className={`mt-2 text-3xl font-bold ${card.num}`}>{card.count}</p>
                        </div>
                      ))}
                    </div>

                    {/* Severity breakdown */}
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <h3 className="mb-3 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-gray-500">
                        <TrendingUp className="h-4 w-4 text-gray-500" /> Severity Breakdown
                      </h3>
                      <div className="grid grid-cols-5 gap-2">
                        {Object.entries(reviewStats.severityBreakdown).map(([sev, count]) => {
                          const colors: Record<string, string> = { critical: "text-red-600 bg-red-50 border-red-200", high: "text-orange-600 bg-orange-50 border-orange-200", medium: "text-yellow-600 bg-yellow-50 border-yellow-200", low: "text-blue-600 bg-blue-50 border-blue-200", info: "text-gray-600 bg-gray-50 border-gray-200" };
                          return (
                            <div key={sev} className={`rounded-lg border p-3 text-center ${colors[sev] || "text-gray-600 bg-gray-50 border-gray-200"}`}>
                              <p className="text-2xl font-bold">{count}</p>
                              <p className="mt-1 text-[10px] font-semibold capitalize">{sev}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Important notice — same as PentestReportConfirmationModal */}
                    <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-4 flex gap-3">
                      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                      <div className="text-[12px] text-amber-900">
                        <p className="font-bold mb-0.5">Important Notice</p>
                        <p>The status of the findings you see here will be reflected in the generated pentest report. If you want, you can close all remaining findings and come back later to request the report.</p>
                      </div>
                    </div>

                    {/* Findings table with search + filters */}
                    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                      <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b border-gray-200 gap-3">
                        <h3 className="text-[11px] font-black uppercase tracking-widest text-gray-600 shrink-0">
                          Findings · {filteredReviewReports.length} / {reviewStats.total}
                        </h3>
                        <div className="flex items-center gap-2 flex-1 justify-end flex-wrap">
                          {/* Severity filter */}
                          <select value={reviewSeverityFilter} onChange={e => setReviewSeverityFilter(e.target.value as any)}
                            className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-gray-700 focus:border-blue-400 focus:outline-none">
                            <option value="all">All Severities</option>
                            <option value="critical">Critical</option>
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                            <option value="info">Info</option>
                          </select>
                          {/* Status filter */}
                          <select value={reviewStatusFilter} onChange={e => setReviewStatusFilter(e.target.value as any)}
                            className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-gray-700 focus:border-blue-400 focus:outline-none">
                            <option value="all">All Statuses</option>
                            <option value="open">Open</option>
                            <option value="closed">Closed</option>
                            <option value="accept_risk">Accept Risk</option>
                            <option value="non_actionable">Non-Actionable</option>
                          </select>
                          {/* Search */}
                          <div className="relative flex items-center">
                            <Search className="absolute left-2.5 h-3 w-3 text-gray-400" />
                            <input type="text" value={reviewSearch} onChange={e => setReviewSearch(e.target.value)} placeholder="Search findings…"
                              className="rounded-lg border border-gray-200 bg-gray-50 pl-7 pr-3 py-1.5 text-[11px] text-gray-700 placeholder:text-gray-400 focus:border-blue-400 focus:bg-white focus:outline-none w-44 transition-all" />
                          </div>
                        </div>
                      </div>

                      <div className="max-h-72 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-200">
                        <table className="w-full">
                          <thead className="sticky top-0 bg-gray-50 border-b border-gray-100">
                            <tr>
                              <th className="px-5 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500 w-[40%]">Finding</th>
                              <th className="px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500 w-[15%]">ID</th>
                              <th className="px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500 w-[20%]">Severity</th>
                              <th className="px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500 w-[20%]">Status</th>
                              <th className="px-4 py-2.5 w-[5%]"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {filteredReviewReports.length > 0 ? filteredReviewReports.map(report => {
                              const sev = getDynamicSeverityCategory(report);
                              const status = getCategorizedStatus(report);
                              return (
                                <tr key={report.report_id} className="hover:bg-gray-50 transition-colors group">
                                  <td className="px-5 py-3">
                                    <button type="button" onClick={() => navigate(`/dashboard/reports/${report.report_id}`)}
                                      className="text-left text-[12px] font-semibold text-blue-600 hover:text-blue-800 hover:underline line-clamp-2 leading-snug text-wrap">
                                      {report.report_title || `Report #${report.report_id}`}
                                    </button>
                                  </td>
                                  <td className="px-4 py-3 text-[11px] text-gray-500 font-mono">#{report.report_id}</td>
                                  <td className="px-4 py-3">
                                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold capitalize ${sev.color}`}>{sev.label}</span>
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${status.color}`}>{status.label}</span>
                                  </td>
                                  <td className="px-4 py-3">
                                    <button type="button" onClick={() => window.open(`/dashboard/reports/${report.report_id}`, "_blank")}
                                      className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-blue-600">
                                      <ExternalLink className="h-3.5 w-3.5" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            }) : (
                              <tr><td colSpan={5} className="px-5 py-8 text-center text-[12px] text-gray-400 italic">No findings match your filters</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Confirmation checkbox — same mechanic as PentestReportConfirmationModal */}
                    <div className={`flex items-start gap-3 rounded-xl border-2 p-4 transition-all duration-300 ${highlightCheckbox ? "border-red-400 bg-red-50 shadow-md" : "border-blue-200 bg-blue-50"}`}>
                      <div className="mt-0.5 cursor-pointer" onClick={() => { setIsConfirmed(!isConfirmed); if (!isConfirmed) setHighlightCheckbox(false); }}>
                        <Checkbox checked={isConfirmed} />
                      </div>
                      <label className="cursor-pointer text-[12px] text-gray-700 leading-relaxed" onClick={() => { setIsConfirmed(!isConfirmed); if (!isConfirmed) setHighlightCheckbox(false); }}>
                        <span className="font-bold text-gray-900">I confirm that</span> I have reviewed all the findings shown above and I am satisfied with their current status. I understand the report will be generated based on this data.
                      </label>
                    </div>
                  </>
                )}
              </motion.div>
            )}

          </AnimatePresence>
        </div>

        {/* ── FOOTER ── */}
        <div className="shrink-0 flex items-center justify-between border-t border-gray-200 bg-gray-50 px-7 py-4">
          <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
            <AlertCircle className="h-4 w-4 text-blue-500 shrink-0" />
            <span>Admins, Sub-Admins, and QA roles can generate scoped reports.</span>
          </div>
          <div className="flex items-center gap-2.5">
            {step > 1 && (
              <button type="button" onClick={() => setStep(step - 1)} disabled={submitting}
                className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all disabled:opacity-50">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
            )}
            {step < 4 ? (
              <button type="button" onClick={handleNext}
                className="group flex items-center gap-1.5 rounded-lg bg-blue-600 px-5 py-2 text-xs font-bold text-white shadow-sm hover:bg-blue-700 transition-all active:scale-95">
                Next <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
              </button>
            ) : (
              <div className="flex gap-2.5">
                <button type="button" onClick={onClose} disabled={submitting}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-all disabled:opacity-50">
                  Cancel
                </button>
                <button type="button" onClick={handleSubmit} disabled={submitting || loadingReview}
                  className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-5 py-2 text-xs font-bold text-white shadow-sm hover:bg-blue-700 transition-all active:scale-95 disabled:bg-gray-400 disabled:cursor-not-allowed">
                  {submitting ? "Requesting…" : "Generate Report"}
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};
